import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { getPillPointCategories } from '../services/pillpointDataLoader';
import { buildCategoryRoute, getCategoryIcon } from '../utils/categoryUtils';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const location = useLocation();
  const { getCartCount, getTotalAmount } = useCart();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const loaded = await getPillPointCategories();
        setCategories(Array.isArray(loaded) ? loaded : []);
      } catch (error) {
        setCategories([]);
      }
    };

    loadCategories();
  }, []);

  const getCategoryValue = (category) => {
    if (!category) return '';
    if (category.slug) return category.slug;
    const route = buildCategoryRoute(category) || '';
    return route.replace('/category/', '');
  };

  const navItems = useMemo(() => {
    const featured = categories.slice(0, 4).map((category) => ({
      name: category.name,
      path: buildCategoryRoute(category),
      icon: getCategoryIcon(category)
    }));

    return [
      { name: 'Home', path: '/', icon: '🏠' },
      ...featured,
      { name: 'All Medicines', path: '/products', icon: '💊' },
      { name: 'Blog', path: '/blog', icon: '📰' },
      { name: 'Contact', path: '/contact', icon: '✉️' }
    ];
  }, [categories]);

  const isActive = (path) => {
    if (!path) {
      return false;
    }

    if (path === '/') {
      return location.pathname === '/';
    }

    return location.pathname.startsWith(path);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      return;
    }

    const params = new URLSearchParams();
    params.set('search', trimmedQuery);
    if (selectedCategory) {
      params.set('category', selectedCategory);
    }

    window.location.href = `/products?${params.toString()}`;
  };

  const closeMenu = () => setIsMenuOpen(false);

  const cartCount = getCartCount();
  let cartTotal = '£0';

  try {
    const total = getTotalAmount();
    cartTotal = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(total || 0);    
  } catch (error) {
    cartTotal = '£0';
  }

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-teal-600 text-white text-[11px] sm:text-xs">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-1 sm:px-6">
          <span className="tracking-wide uppercase">Trusted UK Pharmacy</span>
          <a href="tel:+44 7877617712" className="font-semibold hover:underline"> 
            +44 7877617712
          </a>
        </div>
      </div>

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-2.5 sm:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-teal-200 hover:text-teal-600 lg:hidden"
              aria-label="Open navigation menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="group flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-600 text-lg font-semibold text-white transition group-hover:bg-teal-700">
                P
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-base font-bold text-slate-900 sm:text-lg">PillPoint</span>
                <span className="text-[10px] font-semibold tracking-[0.2em] text-teal-500">UNITED KINGDOM</span>
              </span>
            </Link>
          </div>

          <div className="hidden flex-1 items-center md:flex">
            <form
              onSubmit={handleSearch}
              className="flex w-full items-center rounded-2xl border border-slate-200 bg-slate-50 transition focus-within:border-teal-300 focus-within:ring-2 focus-within:ring-teal-100"
            >
              <div className="relative flex items-center border-r border-slate-200">
                <select
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(event.target.value)}
                  className="h-full appearance-none bg-transparent pl-4 pr-8 text-sm font-medium text-slate-700 focus:outline-none"
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category.slug || category.name} value={getCategoryValue(category)}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 text-slate-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search medicines, product IDs, or brands"
                className="flex-1 bg-transparent px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
              />
              <button
                type="submit"
                className="rounded-r-2xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-700"
              >
                Search
              </button>
            </form>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/contact"
              className="hidden items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-teal-200 hover:text-teal-600 md:flex"
            >
              Help
            </Link>

            <Link
              to="/cart"
              onClick={() => window.scrollTo(0, 0)}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-teal-50 hover:text-teal-600 sm:h-11 sm:w-11"
              aria-label="View cart">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 3h2l.4 2M7 13h11l2-8H5.4m0 0L7 13m0 0-1.8 3.6A1 1 0 006.1 18h10.8M7 13V6h13" />
              </svg> 
              {cartCount > 0 && ( 
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.1rem] items-center justify-center rounded-full bg-teal-600 px-1 text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
            <span className="hidden text-xs font-semibold text-slate-600 md:inline">{cartTotal}</span>
          </div>
        </div>

        <div className="px-3 pb-2 md:hidden">
          <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
            <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M21 21l-4.8-4.8M10 18a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search medicines or IDs"
              className="flex-1 bg-transparent py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </form>
        </div>

        <div className="hidden border-t border-slate-200 bg-white lg:block">
          <nav className="mx-auto max-w-7xl px-6">
            <ul className="flex items-center gap-2 py-3 text-sm font-semibold text-slate-600">
              {navItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 transition ${isActive(item.path)
                        ? 'bg-teal-50 text-teal-600'
                        : 'hover:bg-slate-100 hover:text-teal-600'
                      }`}
                  >
                    <span className="text-base">{item.icon}</span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 lg:hidden ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          onClick={closeMenu}
        />

        {/* Sidebar - Left to Right */}
        <div
          className={`fixed top-0 left-0 z-[60] h-full w-[calc(100%-3rem)] max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-out lg:hidden ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          {/* Header with Brand */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-teal-600 to-teal-500">
            <div className="flex items-center gap-2">
              <span className="text-xl">💊</span>
              <h2 className="text-lg font-bold text-white">PillPointUK</h2>
            </div>
            <button
              type="button"
              onClick={closeMenu}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close navigation menu"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 overflow-y-auto px-4 py-4 h-[calc(100%-64px)]">
            <div className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => { closeMenu(); window.scrollTo(0, 0); }}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm font-semibold text-slate-700 transition hover:border-teal-200 hover:text-teal-600"
                >
                  <span className="text-lg">{item.icon}</span>
                  {item.name}
                </Link>
              ))}
            </div>

            <div className="rounded-2xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-teal-700">
              <p className="font-semibold">Need help ordering?</p>
              <p className="mt-1 text-xs text-teal-500">Chat with us on WhatsApp or call our UK support team.</p>
              <div className="mt-3 flex flex-col gap-2 text-sm">
                <a href="https://wa.me/447877617712" className="inline-flex items-center gap-2 text-teal-600 hover:underline">
                  <span>💬</span>
                  WhatsApp Support
                </a>
                <a href="tel:+447877617712" className="inline-flex items-center gap-2 text-teal-600 hover:underline">
                  <span>📞</span>
                  +44 7877617712
                </a>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Policies & Info</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Link
                  to="/privacy"
                  onClick={() => { closeMenu(); window.scrollTo(0, 0); }}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 font-medium text-slate-700 transition hover:border-teal-200 hover:bg-white hover:text-teal-600"
                >
                  <span className="text-base">🔒</span>
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  onClick={() => { closeMenu(); window.scrollTo(0, 0); }}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 font-medium text-slate-700 transition hover:border-teal-200 hover:bg-white hover:text-teal-600"
                >
                  <span className="text-base">📜</span>
                  Terms
                </Link>
                <Link
                  to="/shipping"
                  onClick={() => { closeMenu(); window.scrollTo(0, 0); }}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 font-medium text-slate-700 transition hover:border-teal-200 hover:bg-white hover:text-teal-600"
                >
                  <span className="text-base">🚚</span>
                  Shipping
                </Link>
                <Link
                  to="/contact"
                  onClick={() => { closeMenu(); window.scrollTo(0, 0); }}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 font-medium text-slate-700 transition hover:border-teal-200 hover:bg-white hover:text-teal-600"
                >
                  <span className="text-base">🤝</span>
                  Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    </header>
  );
};

export default Header;
